import { HandCoins, History, Plus, X, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import AppShell from '../app/AppShell';
import {
  CustomDatePicker,
  CustomSelect,
  ActionIconButton,
  FeedbackMessage,
  FilterField,
  FilterPanel,
  ModalBackdrop,
  SaveConfirmationModal,
  ToastStack,
} from '../components/common';
import { cashAdvanceFormInitialValues } from '../data/cashAdvanceConstants';
import useCancelConfirmation from '../hooks/useCancelConfirmation';
import useCashAdvances from '../hooks/useCashAdvances';
import useSaveConfirmation from '../hooks/useSaveConfirmation';
import {
  fetchSupplierBalancings,
  fetchSupplierSavingsReleases,
} from '../services/purchaseOrderService';
import { hasChangedFormValues } from '../utils/formDirty';
import { formatPeso, formatShortDate } from '../utils/formatters';

const initialFilterValues = {
  search: '',
  status: 'Open',
  date: '',
};

function CashAdvancePage({ navigationItems, activePath, activeSearch = '', onNavigate }) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [historyState, setHistoryState] = useState({
    isOpen: false,
    record: null,
    rows: [],
    isLoading: false,
    error: '',
  });
  const [cancelingCashAdvance, setCancelingCashAdvance] = useState(null);
  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [appliedFilters, setAppliedFilters] = useState(initialFilterValues);
  const saveConfirmation = useSaveConfirmation();
  const cancelConfirmation = useCancelConfirmation();
  const {
    records,
    supplierOptions,
    formValues,
    formErrors,
    isLoading,
    isSaving,
    cancelingRecordId,
    loadError,
    submitError,
    successMessage,
    realtimeMessage,
    hasSuppliers,
    handleCancelRecord,
    handleFormChange,
    handleSubmit,
    handleReset,
  } = useCashAdvances();
  const toasts = [
    loadError ? { id: 'ca-load-error', tone: 'error', message: loadError } : null,
    submitError ? { id: 'ca-submit-error', tone: 'error', message: submitError } : null,
    successMessage ? { id: 'ca-success', tone: 'success', message: successMessage } : null,
    realtimeMessage ? { id: 'ca-realtime-refresh', tone: 'info', message: realtimeMessage } : null,
  ].filter(Boolean);
  const filteredRecords = useMemo(
    () => filterCashAdvanceRecords(records, appliedFilters),
    [records, appliedFilters]
  );
  const pageSize = 5;
  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedRecords = useMemo(
    () =>
      filteredRecords.slice(
        (safeCurrentPage - 1) * pageSize,
        safeCurrentPage * pageSize
      ),
    [filteredRecords, safeCurrentPage]
  );
  const searchSuggestions = useMemo(
    () => buildCashAdvanceSearchSuggestions(records),
    [records]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(activeSearch);
    const searchText = getCashAdvanceSearchTextFromParams(searchParams, records);

    if (!searchText) {
      return;
    }

    const nextFilters = {
      ...initialFilterValues,
      search: searchText,
      status: 'All',
    };

    setFilterValues(nextFilters);
    setAppliedFilters(nextFilters);
    setCurrentPage(1);
  }, [activeSearch, records]);

  async function handleCreate(event) {
    const didCreate = await handleSubmit(event);

    if (didCreate) {
      setIsFormOpen(false);
    }
  }

  function handleCloseForm() {
    handleReset();
    setIsFormOpen(false);
  }

  function handleFilterChange(event) {
    const { name, value } = event.target;

    setFilterValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    if (name === 'search' || name === 'status') {
      setAppliedFilters((currentValues) => ({
        ...currentValues,
        [name]: value,
      }));
      setCurrentPage(1);
    }
  }

  function handleFilterSubmit(event) {
    event.preventDefault();
    setAppliedFilters(filterValues);
    setCurrentPage(1);
  }

  function handleFilterReset() {
    setFilterValues(initialFilterValues);
    setAppliedFilters(initialFilterValues);
    setCurrentPage(1);
  }

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      activeSearch={activeSearch}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
                Cash Advance
              </h2>
              <p className="m-0 mt-2 text-[0.96rem] text-[var(--theme-text-muted)]">
                Record supplier CA requests and monitor open balances.
              </p>
            </div>
            <button
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-[var(--theme-accent)] px-5 text-[0.94rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)]"
              type="button"
              onClick={() => setIsFormOpen(true)}
              disabled={!hasSuppliers}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add C.A.
            </button>
          </div>

          {!hasSuppliers && !isLoading ? (
            <FeedbackMessage
              tone="info"
              message="Add at least one supplier first before recording a CA."
            />
          ) : null}

          <CashAdvanceFilters
            values={filterValues}
            suggestions={searchSuggestions}
            onChange={handleFilterChange}
            onSubmit={handleFilterSubmit}
            onReset={handleFilterReset}
          />

          <CashAdvanceTable
            records={paginatedRecords}
            isLoading={isLoading}
            cancelingRecordId={cancelingRecordId}
            onViewHistory={handleOpenHistory}
            onCancelRecord={setCancelingCashAdvance}
          />
          <CashAdvancePagination
            totalRecords={filteredRecords.length}
            pageSize={pageSize}
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </section>

      {isFormOpen ? (
        <CashAdvanceModal
          values={formValues}
          errors={formErrors}
          supplierOptions={supplierOptions}
          isSaving={isSaving}
          submitError={submitError}
          onChange={handleFormChange}
          onClose={(event) =>
            cancelConfirmation.requestCancel(event, {
              shouldConfirm: () =>
                hasChangedFormValues(formValues, cashAdvanceFormInitialValues),
              onCancel: handleCloseForm,
              title: 'Cancel cash advance?',
              message: 'You have unsaved cash advance inputs. Discard them?',
            })
          }
          onSubmit={(event) =>
            saveConfirmation.requestConfirmation(event, handleCreate, {
              title: 'Save cash advance?',
              message: 'Please confirm the cash advance details before saving.',
              confirmLabel: 'Save C.A.',
            })
          }
        />
      ) : null}
      {historyState.isOpen ? (
        <CashAdvanceHistoryModal
          record={historyState.record}
          rows={historyState.rows}
          isLoading={historyState.isLoading}
          error={historyState.error}
          onClose={handleCloseHistory}
        />
      ) : null}
      {cancelingCashAdvance ? (
        <SaveConfirmationModal
          isOpen
          title="Cancel cash advance?"
          message={`This will cancel the CA record for ${cancelingCashAdvance.supplierName || 'this supplier'}. Continue?`}
          confirmLabel="Cancel C.A."
          confirmingLabel="Cancelling..."
          cancelLabel="Keep C.A."
          isConfirming={cancelingRecordId === cancelingCashAdvance.id}
          onCancel={() => setCancelingCashAdvance(null)}
          onConfirm={async () => {
            const didCancel = await handleCancelRecord(cancelingCashAdvance);

            if (didCancel) {
              setCancelingCashAdvance(null);
            }
          }}
        />
      ) : null}
      <SaveConfirmationModal {...saveConfirmation.modalProps} />
      <SaveConfirmationModal {...cancelConfirmation.modalProps} />
    </AppShell>
  );

  async function handleOpenHistory(record) {
    setHistoryState({
      isOpen: true,
      record,
      rows: [],
      isLoading: true,
      error: '',
    });

    try {
      const [supplierBalancings, supplierSavingsReleases] = await Promise.all([
        fetchSupplierBalancings(),
        fetchSupplierSavingsReleases(),
      ]);
      const rows = buildCashAdvanceBalancingHistoryRows(
        record,
        supplierBalancings,
        supplierSavingsReleases
      );

      setHistoryState({
        isOpen: true,
        record,
        rows,
        isLoading: false,
        error: '',
      });
    } catch (error) {
      setHistoryState({
        isOpen: true,
        record,
        rows: [],
        isLoading: false,
        error: error.message,
      });
    }
  }

  function handleCloseHistory() {
    setHistoryState({
      isOpen: false,
      record: null,
      rows: [],
      isLoading: false,
      error: '',
    });
  }
}

function CashAdvanceFilters({ values, suggestions, onChange, onSubmit, onReset }) {
  return (
    <FilterPanel
      onSubmit={onSubmit}
      onReset={onReset}
      layoutClassName="grid gap-2.5 md:grid-cols-2 xl:grid-cols-[minmax(300px,1.2fr)_180px_190px_190px]"
    >
      <FilterField
        label="Search"
        name="search"
        type="search"
        value={values.search}
        onChange={onChange}
        placeholder="Search supplier, amount, remarks"
        suggestions={suggestions}
      />
      <FilterField
        label="Status"
        name="status"
        value={values.status}
        onChange={onChange}
        options={[
          { value: 'All', label: 'All Statuses' },
          { value: 'Open', label: 'Open' },
          { value: 'Paid', label: 'Paid' },
          { value: 'Cancelled', label: 'Cancelled' },
        ]}
      />
      <FilterField
        label="C.A. Date"
        name="date"
        type="date"
        value={values.date}
        onChange={onChange}
      />
    </FilterPanel>
  );
}

function CashAdvanceTable({
  records,
  isLoading,
  cancelingRecordId,
  onViewHistory,
  onCancelRecord,
}) {
  return (
    <div className="overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading>C.A. Date</TableHeading>
              <TableHeading>Supplier</TableHeading>
              <TableHeading>Total C.A./Loan</TableHeading>
              <TableHeading>Deducted</TableHeading>
              <TableHeading>Remaining</TableHeading>
              <TableHeading>Status</TableHeading>
              <TableHeading>Remarks</TableHeading>
              <TableHeading>Action</TableHeading>
            </tr>
          </thead>
          <tbody>
            {records.length ? (
              records.map((record) => (
                <tr
                  key={record.id}
                  className="border-t border-[var(--theme-border-soft)] transition-colors hover:bg-[var(--theme-surface-soft)]"
                >
                  <TableCell>{formatShortDate(record.caDate)}</TableCell>
                  <TableCell>{record.supplierName || '-'}</TableCell>
                  <TableCell>{formatPeso(record.amount)}</TableCell>
                  <TableCell>{formatPeso(record.paidAmount || 0)}</TableCell>
                  <TableCell>{formatPeso(record.remainingAmount ?? record.amount)}</TableCell>
                  <TableCell>
                    <CashAdvanceStatusBadge status={record.status} />
                  </TableCell>
                  <TableCell>
                    <TruncatedRemarks value={record.remarks} />
                  </TableCell>
                  <TableCell>
                    <CashAdvanceActions
                      record={record}
                      cancelingRecordId={cancelingRecordId}
                      onViewHistory={onViewHistory}
                      onCancelRecord={onCancelRecord}
                    />
                  </TableCell>
                </tr>
              ))
            ) : (
              <tr className="border-t border-[var(--theme-border-soft)]">
                <td className="px-6 py-14" colSpan={8}>
                  <EmptyState isLoading={isLoading} />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 p-4 md:hidden">
        {records.length ? (
          records.map((record) => (
            <article
              key={record.id}
              className="rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-3 transition duration-150 hover:-translate-y-0.5 hover:bg-[var(--theme-surface-soft)] hover:shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.11)]"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <strong className="block text-[1rem] text-[var(--theme-text)]">
                    {record.supplierName || '-'}
                  </strong>
                  <span className="mt-1 block text-[0.9rem] text-[var(--theme-text-muted)]">
                    {formatShortDate(record.caDate)}
                  </span>
                </div>
                <CashAdvanceStatusBadge status={record.status} />
              </div>
              <div className="mt-4 grid gap-2 text-[0.92rem] text-[var(--theme-text-subtle)]">
                <RecordMeta label="Total C.A./Loan" value={formatPeso(record.amount)} />
                <RecordMeta label="Deducted" value={formatPeso(record.paidAmount || 0)} />
                <RecordMeta
                  label="Remaining"
                  value={formatPeso(record.remainingAmount ?? record.amount)}
                />
                <RecordMeta label="Remarks" value={<TruncatedRemarks value={record.remarks} />} />
                <div className="mt-2 flex justify-end">
                  <CashAdvanceActions
                    record={record}
                    cancelingRecordId={cancelingRecordId}
                    onViewHistory={onViewHistory}
                    onCancelRecord={onCancelRecord}
                  />
                </div>
              </div>
            </article>
          ))
        ) : (
          <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
            <EmptyState isLoading={isLoading} />
          </div>
        )}
      </div>
    </div>
  );
}

function CashAdvancePagination({
  totalRecords,
  pageSize,
  currentPage,
  totalPages,
  onPageChange,
}) {
  if (totalRecords <= pageSize) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded-[18px] border border-[var(--theme-border)] bg-white px-4 py-3 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.06)] sm:flex-row sm:items-center sm:justify-between">
      <span className="text-[0.88rem] font-medium text-[var(--theme-text-muted)]">
        Showing {(currentPage - 1) * pageSize + 1}-
        {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}
      </span>
      <div className="flex items-center gap-2">
        <button
          className="min-h-[38px] rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.88rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          onClick={() => onPageChange((page) => Math.max(1, page - 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className="min-w-[74px] text-center text-[0.88rem] font-semibold text-[var(--theme-text-subtle)]">
          {currentPage} / {totalPages}
        </span>
        <button
          className="min-h-[38px] rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.88rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          onClick={() => onPageChange((page) => Math.min(totalPages, page + 1))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function CashAdvanceActions({
  record,
  cancelingRecordId,
  onViewHistory,
  onCancelRecord,
}) {
  const canCancel = canCancelCashAdvance(record);

  return (
    <div className="flex items-center justify-end gap-1.5">
      <ActionIconButton
        onClick={() => onViewHistory(record)}
        label={`View C.A. deduction history for ${record.supplierName || 'supplier'}`}
        title={`View C.A. deduction history for ${record.supplierName || 'supplier'}`}
      >
        <History className="h-4 w-4" aria-hidden="true" />
      </ActionIconButton>
      {canCancel ? (
        <ActionIconButton
          tone="danger"
          onClick={() => onCancelRecord(record)}
          label={`Cancel C.A. for ${record.supplierName || 'supplier'}`}
          title={`Cancel C.A. for ${record.supplierName || 'supplier'}`}
          disabled={cancelingRecordId === record.id}
        >
          <XCircle className="h-4 w-4" aria-hidden="true" />
        </ActionIconButton>
      ) : null}
    </div>
  );
}

function CashAdvanceModal({
  values,
  errors,
  supplierOptions,
  isSaving,
  submitError,
  onChange,
  onClose,
  onSubmit,
}) {
  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.26)] backdrop-blur-[3px]">
      <form
        className="w-full max-w-2xl overflow-hidden rounded-[24px] border border-[var(--theme-border-soft)] bg-white shadow-[0_28px_80px_rgba(var(--theme-shadow-rgb),0.24)]"
        onSubmit={onSubmit}
      >
        <div className="border-b border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--theme-accent)] text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.24)]">
                <HandCoins className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 className="m-0 text-[1.22rem] font-semibold text-[var(--theme-text)]">
                  Add Cash Advance
                </h3>
                <p className="m-0 mt-1 text-[0.9rem] leading-5 text-[var(--theme-text-muted)]">
                  Record a supplier-level C.A. and monitor open balances.
                </p>
              </div>
            </div>
            <button
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#e1d5ca] bg-white text-[var(--theme-text-subtle)] shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.08)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-60"
              type="button"
              onClick={onClose}
              disabled={isSaving}
              aria-label="Close C.A. form"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          {submitError ? (
            <div className="mb-5">
              <FeedbackMessage tone="error" message={submitError} />
            </div>
          ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <CustomSelect
            label="Supplier"
            name="supplierId"
            value={values.supplierId}
            error={errors.supplierId}
            onChange={onChange}
            options={supplierOptions}
            placeholder="Select supplier"
            disabled={isSaving}
          />

          <CustomDatePicker
            label="C.A. Date"
            name="caDate"
            value={values.caDate}
            error={errors.caDate}
            onChange={onChange}
            disabled={isSaving}
          />

          <div className="sm:col-span-2">
            <div className="rounded-[18px] border border-[#dcebdc] bg-[#f4fbf4] px-4 py-3.5">
              <span className="block text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-[#6f8a70]">
                Status
              </span>
              <div className="mt-3">
                <CashAdvanceStatusBadge status="Open" />
              </div>
            </div>
          </div>

          <div className="sm:col-span-2 rounded-[20px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_14px_34px_rgba(var(--theme-shadow-rgb),0.06)]">
            <FieldGroup label="C.A. Amount" error={errors.amount}>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[1rem] font-semibold text-[var(--theme-text-muted)]">
                  ₱
                </span>
                <input
                  className={`${getInputClassName(errors.amount)} pl-9 text-[1.05rem] font-semibold`}
                  name="amount"
                  type="text"
                  inputMode="decimal"
                  value={values.amount}
                  onChange={onChange}
                  placeholder="0.00"
                  disabled={isSaving}
                />
              </div>
            </FieldGroup>
          </div>

          <div className="sm:col-span-2">
            <FieldGroup label="Remarks" error={errors.remarks}>
              <textarea
                className={`${getInputClassName(errors.remarks)} min-h-[92px] resize-none py-3`}
                name="remarks"
                value={values.remarks}
                onChange={onChange}
                placeholder="Optional notes"
                disabled={isSaving}
              />
            </FieldGroup>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            className="min-h-[46px] rounded-full border border-[var(--theme-border)] bg-white px-6 text-[0.94rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-60 sm:min-w-[140px]"
            type="button"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="min-h-[46px] rounded-full bg-[var(--theme-accent)] px-6 text-[0.94rem] font-semibold text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.22)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] disabled:shadow-none sm:min-w-[180px]"
            type="submit"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save C.A.'}
          </button>
        </div>
        </div>
      </form>
    </ModalBackdrop>
  );
}

function CashAdvanceHistoryModal({ record, rows, isLoading, error, onClose }) {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedRows = rows.slice(
    (safeCurrentPage - 1) * pageSize,
    safeCurrentPage * pageSize
  );
  const totalDeducted = rows.reduce(
    (total, row) => total + Number(row.deductedAmount || 0),
    0
  );

  return (
    <ModalBackdrop backdropClassName="bg-[rgba(33,27,22,0.26)] backdrop-blur-[3px]">
      <div className="w-full max-w-4xl overflow-hidden rounded-[24px] border border-[var(--theme-border-soft)] bg-white shadow-[0_28px_80px_rgba(var(--theme-shadow-rgb),0.24)]">
        <div className="border-b border-[var(--theme-border-soft)] bg-[var(--theme-surface-muted)] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--theme-accent)] text-white shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.24)]">
                <History className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h3 className="m-0 text-[1.2rem] font-semibold text-[var(--theme-text)]">
                  C.A. Deduction History
                </h3>
                <p className="m-0 mt-1 text-[0.9rem] leading-5 text-[var(--theme-text-muted)]">
                  {record?.supplierName || '-'} / {formatShortDate(record?.caDate)}
                </p>
              </div>
            </div>
            <button
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#e1d5ca] bg-white text-[var(--theme-text-subtle)] shadow-[0_8px_20px_rgba(var(--theme-shadow-rgb),0.08)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
              aria-label="Close C.A. deduction history"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            <SummaryTile label="Selected C.A." value={formatPeso(record?.amount || 0)} />
            <SummaryTile label="This C.A. Deductions" value={formatPeso(totalDeducted)} />
          </div>

          {error ? (
            <div className="mt-4">
              <FeedbackMessage tone="error" message={error} />
            </div>
          ) : null}

          <div className="mt-5 overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
            <div className="hidden max-h-[420px] overflow-auto md:block">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-[var(--theme-accent-panel)]">
                    <TableHeading>Balancing Created Date</TableHeading>
                    <TableHeading>Source</TableHeading>
                    <TableHeading>Included Orders</TableHeading>
                    <TableHeading>Status</TableHeading>
                    <TableHeading>C.A. Deducted</TableHeading>
                    <TableHeading>Total Balance</TableHeading>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.length ? (
                    paginatedRows.map((row) => (
                      <tr key={row.id} className="border-t border-[var(--theme-border-soft)]">
                        <TableCell>{formatShortDate(row.createdAt)}</TableCell>
                        <TableCell>{row.source || '-'}</TableCell>
                        <TableCell>{formatNumber(row.purchaseOrderCount, 0)}</TableCell>
                        <TableCell>{row.status || '-'}</TableCell>
                        <TableCell>{formatPeso(row.deductedAmount)}</TableCell>
                        <TableCell>{formatPeso(row.totalBalance)}</TableCell>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-t border-[var(--theme-border-soft)]">
                      <td className="px-6 py-14" colSpan={6}>
                        <EmptyHistoryState isLoading={isLoading} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid max-h-[420px] gap-3 overflow-auto p-4 md:hidden">
              {paginatedRows.length ? (
                paginatedRows.map((row) => (
                  <article
                    key={row.id}
                    className="rounded-[16px] border border-[var(--theme-border-soft)] bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <strong className="text-[var(--theme-text-strong)]">
                        {formatNumber(row.purchaseOrderCount, 0)} Included Orders
                      </strong>
                      <span className="text-[0.86rem] text-[var(--theme-text-muted)]">
                        {formatShortDate(row.createdAt)}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 text-[0.9rem]">
                      <RecordMeta label="Status" value={row.status || '-'} />
                      <RecordMeta label="Source" value={row.source || '-'} />
                      <RecordMeta label="C.A. Deducted" value={formatPeso(row.deductedAmount)} />
                      <RecordMeta label="Total Balance" value={formatPeso(row.totalBalance)} />
                    </div>
                  </article>
                ))
              ) : (
                <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-white px-5 py-12">
                  <EmptyHistoryState isLoading={isLoading} />
                </div>
              )}
            </div>
          </div>

          {rows.length > pageSize ? (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-[0.88rem] font-medium text-[var(--theme-text-muted)]">
                Showing {(safeCurrentPage - 1) * pageSize + 1}-
                {Math.min(safeCurrentPage * pageSize, rows.length)} of {rows.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="min-h-[38px] rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.88rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={safeCurrentPage === 1}
                >
                  Previous
                </button>
                <span className="min-w-[74px] text-center text-[0.88rem] font-semibold text-[var(--theme-text-subtle)]">
                  {safeCurrentPage} / {totalPages}
                </span>
                <button
                  className="min-h-[38px] rounded-full border border-[var(--theme-border)] bg-white px-4 text-[0.88rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  disabled={safeCurrentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}

          <div className="mt-5 flex justify-end">
            <button
              className="min-h-[44px] rounded-full border border-[var(--theme-border)] bg-white px-6 text-[0.94rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-[18px] border border-[var(--theme-border-soft)] bg-white px-4 py-3.5">
      <span className="block text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-2 block text-[1.08rem] text-[var(--theme-text-strong)]">{value}</strong>
    </div>
  );
}

function FieldGroup({ label, error, children }) {
  return (
    <label className="grid gap-2">
      <span className="text-[0.88rem] font-medium text-[var(--theme-text-subtle)]">{label}</span>
      {children}
      {error ? <span className="text-[0.82rem] text-[#bb5745]">{error}</span> : null}
    </label>
  );
}

function TableHeading({ children }) {
  return (
    <th className="whitespace-nowrap px-3 py-2.5 text-left text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4">
      {children}
    </th>
  );
}

function TableCell({ children }) {
  return (
    <td className="whitespace-nowrap px-3 py-2 align-middle text-[0.84rem] text-[#222222] lg:px-4">
      {children}
    </td>
  );
}

function RecordMeta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <div className="text-right font-medium text-[#2a2a2a]">{value}</div>
    </div>
  );
}

function TruncatedRemarks({ value }) {
  const remarks = String(value || '').trim();

  if (!remarks) {
    return '-';
  }

  return (
    <span className="block max-w-[150px] truncate md:max-w-[170px]" title={remarks}>
      {remarks}
    </span>
  );
}

function CashAdvanceStatusBadge({ status }) {
  const colorClassName = {
    Open: 'bg-[#eef6ec] text-[#3f7a44]',
    Paid: 'bg-[#f1f4f7] text-[#5a6673]',
    Cancelled: 'bg-[#fff1ed] text-[#bf5d4b]',
  }[status] || 'bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]';

  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-[0.82rem] font-semibold ${colorClassName}`}>
      {status}
    </span>
  );
}

function EmptyState({ isLoading }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <HandCoins className="h-6 w-6" aria-hidden="true" />
      </div>
      <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
        {isLoading ? 'Loading CA records...' : 'No CA records found'}
      </strong>
    </div>
  );
}

function EmptyHistoryState({ isLoading }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <History className="h-6 w-6" aria-hidden="true" />
      </div>
      <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
        {isLoading ? 'Loading supplier balancing records...' : 'No supplier balancing deductions traced yet'}
      </strong>
    </div>
  );
}

function buildCashAdvanceBalancingHistoryRows(
  record,
  supplierBalancings,
  supplierSavingsReleases = []
) {
  const selectedLoanId = Number(record?.id);
  const selectedLoanAmount = Number(record?.amount || 0);

  if (!selectedLoanId || !Array.isArray(supplierBalancings)) {
    return [];
  }

  const balancingDeductionRows = supplierBalancings
    .flatMap((balancing) => {
      const applications = Array.isArray(balancing.cashAdvanceApplications)
        ? balancing.cashAdvanceApplications
        : [];

      return applications
        .filter((application) => Number(application.loanId) === selectedLoanId)
        .map((application) => ({
          id: `${balancing.id}-${application.loanId}`,
          balancingId: balancing.id,
          createdAt: balancing.createdAt,
          source: 'Supplier Balancing',
          status: balancing.status,
          purchaseOrderCount: Array.isArray(balancing.purchaseOrderIds)
            ? balancing.purchaseOrderIds.length
            : 0,
          deductedAmount: Number(application.amount || 0),
        }));
    });
  const savingsReleaseDeductionRows = (Array.isArray(supplierSavingsReleases)
    ? supplierSavingsReleases
    : []
  )
    .filter((release) => String(release.releaseType || '') === 'Cash Advance')
    .flatMap((release) => {
      const applications = Array.isArray(release.cashAdvanceApplications)
        ? release.cashAdvanceApplications
        : [];
      const reversedApplications = Array.isArray(release.reversedCashAdvanceApplications)
        ? release.reversedCashAdvanceApplications
        : [];

      return subtractReversedCashAdvanceApplications(applications, reversedApplications)
        .filter((application) => Number(application.loanId) === selectedLoanId)
        .map((application) => ({
          id: `savings-release-${release.id}-${application.loanId}`,
          balancingId: release.id,
          createdAt: release.releasedAt || release.createdAt,
          source: 'Supplier Savings',
          status: 'C.A. Deducted',
          purchaseOrderCount: Array.isArray(release.supplierBalancingIds)
            ? release.supplierBalancingIds.length
            : 0,
          deductedAmount: Number(application.amount || 0),
        }));
    });
  const deductionRows = [
    ...balancingDeductionRows,
    ...savingsReleaseDeductionRows,
  ]
    .sort((firstRow, secondRow) => {
      const firstTime = new Date(firstRow.createdAt || 0).getTime();
      const secondTime = new Date(secondRow.createdAt || 0).getTime();

      return firstTime - secondTime || Number(firstRow.balancingId) - Number(secondRow.balancingId);
    });

  let remainingBalance = selectedLoanAmount;
  const rowsWithBalance = deductionRows.map((row) => {
    remainingBalance = Math.max(roundMoney(remainingBalance - Number(row.deductedAmount || 0)), 0);

    return {
      ...row,
      totalBalance: remainingBalance,
    };
  });

  return rowsWithBalance
    .sort((firstRow, secondRow) => {
      const firstTime = new Date(firstRow.createdAt || 0).getTime();
      const secondTime = new Date(secondRow.createdAt || 0).getTime();

      return secondTime - firstTime || Number(secondRow.balancingId) - Number(firstRow.balancingId);
    });
}

function subtractReversedCashAdvanceApplications(applications, reversedApplications) {
  const remainingReversalByLoanId = new Map();

  reversedApplications.forEach((application) => {
    const loanId = Number(application.loanId);
    const amount = Number(application.amount || 0);

    if (!loanId || amount <= 0) {
      return;
    }

    remainingReversalByLoanId.set(
      loanId,
      Number(remainingReversalByLoanId.get(loanId) || 0) + amount
    );
  });

  return applications
    .map((application) => {
      const loanId = Number(application.loanId);
      const amount = Number(application.amount || 0);
      const reversedAmount = Number(remainingReversalByLoanId.get(loanId) || 0);

      if (!loanId || amount <= 0) {
        return null;
      }

      if (reversedAmount <= 0) {
        return {
          ...application,
          amount,
        };
      }

      const remainingAmount = roundMoney(Math.max(amount - reversedAmount, 0));
      remainingReversalByLoanId.set(loanId, Math.max(reversedAmount - amount, 0));

      return remainingAmount > 0
        ? {
            ...application,
            amount: remainingAmount,
          }
        : null;
    })
    .filter(Boolean);
}

function filterCashAdvanceRecords(records, filters) {
  const normalizedSearch = normalizeCashAdvanceSearchText(filters.search);

  return records.filter((record) => {
    const matchesSearch =
      !normalizedSearch ||
      [
        record.id,
        record.supplierName,
        record.status,
        record.remarks,
        formatPeso(record.amount),
        formatPeso(record.paidAmount || 0),
        formatPeso(record.remainingAmount ?? record.amount),
        formatShortDate(record.caDate),
      ]
        .filter(Boolean)
        .some((value) => normalizeCashAdvanceSearchText(value).includes(normalizedSearch));
    const matchesStatus =
      filters.status === 'All' ||
      String(record.status || '') === String(filters.status || '');
    const matchesDate = !filters.date || toDateInputValue(record.caDate) === filters.date;

    return matchesSearch && matchesStatus && matchesDate;
  });
}

function getCashAdvanceSearchTextFromParams(searchParams, records) {
  const caId = String(searchParams.get('caId') || '').trim();
  const searchText = String(searchParams.get('search') || '').trim();

  if (!caId) {
    return searchText;
  }

  const matchingRecord = records.find((record) => String(record.id || '') === caId);

  if (!matchingRecord) {
    return searchText || caId;
  }

  return matchingRecord.supplierName || searchText || caId;
}

function canCancelCashAdvance(record) {
  return String(record.status || '') === 'Open' && Number(record.paidAmount || 0) <= 0;
}

function buildCashAdvanceSearchSuggestions(records) {
  const suggestionsByKey = new Map();

  records.forEach((record) => {
    [
      ['supplier', record.supplierName, 'Supplier'],
      ['status', record.status, 'Status'],
      ['amount', formatPeso(record.amount), 'Total C.A./Loan'],
      ['remaining', formatPeso(record.remainingAmount ?? record.amount), 'Remaining'],
    ].forEach(([group, value, detail]) => {
      const normalizedValue = String(value || '').trim();

      if (!normalizedValue) {
        return;
      }

      suggestionsByKey.set(`${group}:${normalizedValue.toLowerCase()}`, {
        group,
        value: normalizedValue,
        label: normalizedValue,
        detail,
        searchValue: getCashAdvanceSuggestionSearchValue(group, record, normalizedValue),
      });
    });
  });

  return Array.from(suggestionsByKey.values())
    .sort((firstSuggestion, secondSuggestion) =>
      firstSuggestion.label.localeCompare(secondSuggestion.label)
    )
    .slice(0, 12);
}

function getCashAdvanceSuggestionSearchValue(group, record, fallbackValue) {
  if (group === 'amount') {
    return String(record.amount ?? fallbackValue);
  }

  if (group === 'remaining') {
    return String(record.remainingAmount ?? record.amount ?? fallbackValue);
  }
  return fallbackValue;
}

function formatNumber(value, maximumFractionDigits = 2) {
  return Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: maximumFractionDigits === 0 ? 0 : 2,
    maximumFractionDigits,
  });
}

function roundMoney(value) {
  return Math.round(Number(value || 0) * 100) / 100;
}

function normalizeCashAdvanceSearchText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[₱,\s]/g, '');
}

function toDateInputValue(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getInputClassName(hasError) {
  return `min-h-[44px] rounded-[14px] border bg-[var(--theme-surface-warm)] px-4 text-[0.94rem] outline-none transition ${hasError
      ? 'border-[#d78b7d] focus:border-[#c76552]'
      : 'border-[var(--theme-border)] focus:border-[var(--theme-text-subtle)]'
    }`;
}

export default CashAdvancePage;
